/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DataPointSelections
 * @hideconstructor
 */
define([
	'underscore',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/DataPointSelectionsAPI',
	'./api/deprecated/DataPointSelectionsAPI',
	'./api/DataPointSelectionsAPISpec',
	'../../../../util/ContentUtil',
	'../../../../lib/@waca/dashboard-common/dist/utils/ContentUtil',
	'./VisSelected',
	'./PendingSelections'
], (_, APIFactory, DataPointSelectionsAPI, DeprecatedDataPointSelectionsAPI, DataPointSelectionsAPISpec, ContentUtil, CommonContentUtil, VisSelected, PendingSelections) => {

	class DataPointSelections extends DataPointSelectionsAPISpec {

		constructor(options){
			super(options);

			this.content = options.content;
			this.dashboard = options.dashboardAPI;

			this.featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');

			this._selectionProvider = null;
		}

		registerSelectionProvider(provider) {
			//TODO: Should this be a list?
			this._selectionProvider = provider;
		}

		getAPI(type) {
			if (type === 'deprecated') {
				if (!this._deprecatedApi) {
					this._deprecatedApi = APIFactory.createAPI(this, [DataPointSelectionsAPI, DeprecatedDataPointSelectionsAPI]);
				}
				return this._deprecatedApi;
			}
			if (type === 'pending') {
				if (!this._pending) {
					const pendingSelections = new PendingSelections();
					this._pending = APIFactory.createAPI(pendingSelections , [DataPointSelectionsAPI]);
				}
				return this._pending;
			}
			if (type === 'utils') {
				if (!this._utils) {
					this._utils = {
						getLegacySelection: this._getLegacySelection.bind(this)
					};
				}
				return this._utils;
			}

			if (!this._api) {
				this._api = APIFactory.createAPI(this, [DataPointSelectionsAPI]);
			}
			return this._api;
		}

		select(selections, transactionToken) {
			if (this._selectionProvider) {
				this._selectionProvider.select(selections, transactionToken);
			} else {
				const legacySelection = this._getLegacySelection(selections, 'update');
				const controller = this._getController();
				controller.select(legacySelection, transactionToken);
				return selections;
			}
		}

		/**
		 * deselect current selections.
		 * @param {*} selections The selected items to deselect
		 * @param {*} transactionToken the transaction Token
		 * @param {*} forceClearSlots if true, clear all slots regardless if there is a selection
		 */
		deselect(selections, transactionToken, forceClearSlots=false) {
			if (this._selectionProvider) {
				this._selectionProvider.deselect(selections, transactionToken, forceClearSlots);
			} else {
				const slotsToClear = this._getSlotsToClear(forceClearSlots ? undefined : selections);
				const legacySelection = this._getLegacySelection(selections, 'remove', slotsToClear);
				const controller = this._getController();
				controller.select(legacySelection, transactionToken);
				return selections;
			}
		}

		_getSelectionValues(selections) {
			// Default. Use the current selections
			return _.chain(selections || this.getSelections())
				.map(selection => selection.categories)
				.flatten()
				.map(selection => selection.value)
				.value();
		}

		_getSlotsToClear(selections) {
			const values = this._getSelectionValues(selections);
			const current = this._getSelectionValues();
			if (values.length === current.length && !_.difference(values, current).length) {
				const visualization = this.content.getFeature('Visualization');
				return visualization.getSlots().getMappedSlotList();
			}
			return [];
		}

		clearAll(transactionToken) {
			if (this._selectionProvider) {
				this._selectionProvider.clearAll();
			} else {
				const selections = this._buildSelections(/*selectedDataItemList*/[], /*strictMatch*/ false) || [];
				return this.deselect(selections, transactionToken, true);
			}
		}

		_getVisualization() {
			if (!this.visualization) {
				this.visualization = this.content.getFeature('Visualization');
			}
			return this.visualization;
		}

		_getController() {
			if (!this.controller) {
				this.controller = this.content.getFeature('SelectionController.deprecated');
			}
			return this.controller;
		}

		_getLegacySelection (selections, command, slotsToClear = []) {
			const legacySelection = {
				command,
				itemIds: [],
				tuple: [],
				edgeSelect: !!_.find(selections, (selection) => selection.edgeSelect),
				multiTuple: !!(selections && selections.length > 1),
				slotsToClear
			};
			selections && selections.forEach(selection => {
				const values = [];
				const addedValues = [];
				selection.categories && selection.categories.forEach(category => {

					if (!legacySelection.itemIds.includes(category.columnId)) {
						legacySelection.itemIds.push(category.columnId);
					}

					if(!addedValues.includes(category.columnId+category.value)){
						values.push({ u: category.value, d: category.label });
						addedValues.push(category.columnId+category.value);
					}
				});
				if (values.length) {
					legacySelection.tuple.push(values);
				}
			});
			return legacySelection;
		}

		getSelections(selectedDataItemList = [], compactForm=false) {
			if (this._selectionProvider) {
				const points = this._selectionProvider.getSelectedPoints(selectedDataItemList.map((di) => di.getColumnId()));
				if (compactForm) {
					//use compact form when no dataItem info is needed (it is passed in)
					return points;
				}
				//Return in payload form.
				return points.map((point) => {
					if (point.from!==undefined || point.to!==undefined) {
						const returnPayload = { ranges: [] };
						returnPayload.ranges.push(this._toPayloadValue(point, selectedDataItemList[0].getColumnId(), selectedDataItemList[0].getId()));
						return returnPayload;
					}
					return {
						categories:
							point && point.length && point.map((tuplePart, i) => {
								return this._toPayloadValue(tuplePart, selectedDataItemList[i].getColumnId(), selectedDataItemList[i].getId());
							}) || []
					};
				});
			} else {
				return this._buildSelections(selectedDataItemList);
			}
		}

		_getScope() {
			const type = this.dashboard.getAppConfig('pageContainerType');
			const page = CommonContentUtil.getPageContent(this.content, type);
			const scope = page && page.getId();
			return scope;
		}

		_getEventGroupId() {
			const eventGroups = this.dashboard.getFeature('EventGroups');
			const eventGroupId = eventGroups.getGroupId(this.content.getId());
			return eventGroupId;
		}

		_getPageContext() {
			if (!this._pageContext) {
				this._pageContext = ContentUtil.getPageContext(this.dashboard);
			}
			return this._pageContext;
		}

		_buildSelections(selectedDataItemList, strictMatch = true) {
			let dataPointSelections = [];
			const dataSource = this._getVisualization().getDataSource();
			const sourceId = dataSource && dataSource.getId();
			if (sourceId) {
				//NOTE: When creating selectors for pageContext, wo DO NOT include this widget's id as the eventSourceId.
				//That would only return selection entries created by this vis.
				//We want to include selection entries that originated from other visualizations because they
				//would also be considered selected (and be highlighted)
				//IFF all items in the page context entry are projected in this vis.
				const selectors = {
					scope: this._getScope(),
					eventGroupId : this._getEventGroupId(),
					sourceId: sourceId,
					origin: 'visualization',
					_strictMatch: strictMatch
				};
				const slots = this._getVisualization().getSlots();
				const mappedDataItemList = slots.getDataItemList();

				/**
				 * If there is either brushing or pending filters collect data points or edge values.
				 * Note: pageContextEntry.getDataPoints() returns an array of array, i.e. an array of tuples,
				 * while pageContextEntry.getValues() returns an array of values.
				 */
				const netPageContext = this._getPageContext().getNetPageContextItems(selectors);
				dataPointSelections.push(...this._getValues(netPageContext, selectedDataItemList, mappedDataItemList));

				if (dataPointSelections.length) {
					dataPointSelections = this._uniqueValues(dataPointSelections);
				}
			}

			return dataPointSelections;
		}

		_getValues(pageContextEntries, selectedDataItemList, mappedDataItemList) {
			let payloadValueList = [];
			let payloadValue = null;
			let dataItem = null;
			if (pageContextEntries && pageContextEntries.length) {
				pageContextEntries.forEach(pEntry => {
					const columnIdList =  pEntry.getItemIds();
					if (pEntry.isDataPointType()) {
						let payloadTupleList = [];
						const tupleList = pEntry.getDataPoints();
						tupleList.forEach(tuple => {
							let payloadTuple = [];
							tuple.forEach((value, valueIdx) => {
								dataItem = this._getDataItem(columnIdList[valueIdx], selectedDataItemList, mappedDataItemList);
								if (dataItem && dataItem.getType() === 'attribute') {
									payloadValue = this._toPayloadValue(value, columnIdList[valueIdx], dataItem.getId());
									payloadTuple.push(payloadValue);
								}
							});
							//This datapoint should be highlited/selected if we've found a projected item that matches every column in the pageContext dataPoint.
							if (pEntry.getItemIds().length <= payloadTuple.length) {
								payloadTupleList.push({
									categories: payloadTuple
								});
							}
						});
						payloadValueList.push(...payloadTupleList);
					} else {
						const values = pEntry.getValues();
						values.forEach(value => {
							dataItem = this._getDataItem(columnIdList[0], selectedDataItemList, mappedDataItemList);
							if (dataItem && dataItem.getType() === 'attribute') {
								payloadValue = this._toPayloadValue(value, columnIdList[0], dataItem.getId());
								payloadValueList.push({
									categories: [payloadValue]
								});
							}
						});
					}
				});
			}
			return payloadValueList;
		}

		_getDataItem(columnId, selectedDataItemList, mappedDataItemList) {
			let dataItem = _.find(selectedDataItemList, dataItem => dataItem.getColumnId() === columnId);
			if (!dataItem) {
				dataItem = _.find(mappedDataItemList, dataItem => dataItem.getColumnId() === columnId);
			}
			return dataItem;
		}

		_toPayloadValue(value, columnId, dataItemId) {
			let resultPayload = {
				dataItemId: dataItemId,
				columnId: columnId
			};

			const isValid = (val) => {
				return val || val === 0;
			};

			if (isValid(value.from) || isValid(value.to)) {
				resultPayload.range = value;
			} else if (value.value !== undefined) {
				resultPayload.value = value.value;
				resultPayload.label = value.label;
			} else if (value.u !== undefined) {
				resultPayload.value = value.u;
				resultPayload.label = value.d;
			}
			if (value.p) {
				resultPayload.parentId = value.p.u;
			}
			return  resultPayload;
		}

		_uniqueValues(dataPointSelections = []) {
			let uniqueValues = [];
			let valuesMap = {};
			dataPointSelections.forEach(dataPoint => {
				const categoryKey = _.pluck(dataPoint.categories, 'value').join();
				if (!valuesMap[categoryKey]) {
					valuesMap[categoryKey] = 1;
					uniqueValues.push(dataPoint);
				}
			});
			return uniqueValues;
		}

		isSelected(selectionPayload) {
			if (this._selectionProvider) {
				return this._selectionProvider.isSelected(selectionPayload);
			}
			const resolvedSelections = this._getLegacySelection(selectionPayload);
			return resolvedSelections.edgeSelect ? this._isEdgeSelected(selectionPayload) : this._isDataPointSelected(resolvedSelections);
		}

		/**
		 * @return {boolean} true if the resolved items are identical to the current selection
		 */
		_isDataPointSelected(resolvedSelections) {
			const selection = this.getSelection(resolvedSelections.itemIds);
			return selection ? selection.isSelected(_.flatten(resolvedSelections.tuple)) : false;
		}

		/**
		 * @return {boolean} true if the resolved items are identical to the current selection
		 */
		_isEdgeSelected(selections) {
			const getSelectedEdgeValues = (columnId) => {
				return this._getController().getSelectedTuples([columnId]);
			};

			// The format of resolved and the result of visSelectionController.getSelectedTuples() do not match.
			// Perform a sequence of operations to transform the output of getSelectedTuples() into the same format as resolved.
			const selectionTuples = _.chain(_.map(this._getVisualization().getSlots().getDataItemList(), dataItem => {
				const columnId = dataItem.getColumnId();
				const values = getSelectedEdgeValues(columnId);
				return _.map(values, value => {
					value.label = _.isUndefined(value.label) ? value.d : value.label;
					value.value = _.isUndefined(value.value) ? value.u : value.value;
					return {
						columnId,
						tupleItem: value
					};
				});

			}))
				.flatten()
				.filter(itemTuplePair => !!itemTuplePair.tupleItem) // Remove items with empty tuples
				.value(); // End sequence of transformations - get final array

			// check if the resolved tuple is a subset of the selectionTuples
			let isSelected = true;
			let categorySelections = _.pluck(selections, 'categories');
			_.each(categorySelections, resolvedTuple => {
				_.each(resolvedTuple, tupleValue => {
					isSelected = isSelected ? !!_.find(selectionTuples, selectedTuple => {
						return selectedTuple.columnId === tupleValue.columnId && selectedTuple.tupleItem.u === tupleValue.value;
					}) : false;
				});
			});
			return isSelected;
		}

		/**
		 * Get a selection object that corresponds to the supplied list of itemIds.
		 * @param itemIds	array of item ids to be considered part of the selection
		 *					If omitted, apply all dataitem
		 * @param {object} options  selection options to describe the selection
		 * @param {boolean} options.includeParents[includeParents=false]- if includes parents; default to false
		 * @param {boolean} options.ignoreEdgeSelections[ignoreEdgeSelections=false] - if includes parents; default to false
		 * @return selection object
		 */
		getSelection(itemIds, options) {
			if (!itemIds) {
				itemIds = ContentUtil.getColumnIdList(this._getVisualization().getSlots().getMappedSlotList(), 'attribute');
			}
			return this._getSelected(itemIds, options);
		}

		/**
		 * Return a proxy object that returns the selected items/tuples as an object for fast lookup.
		 * @param itemIds - the itemIds that form a tuple as an array ['Products', 'Years']
		 *					(also supports a single itemId)
		 * @returns a proxy object that returns an isSelected interface about what is selected by the itemIds.
		 *			or null if no selections exist for any of the set of items.
		 */
		_getSelected(itemIds, options) {
			if (!this._getVisualization().getDataSource()) {
				return null;
			}
			const visualization = this._getVisualization();
			const dataSource = visualization.getDataSource();
			const selected = new VisSelected({
				pageContextAPI: this._getPageContext(),
				selectionProvider: this._selectionProvider,
				itemIds: Array.isArray(itemIds) ? itemIds : [itemIds],
				scope: this._getScope(),
				eventGroupId: this._getEventGroupId(),
				sourceId: dataSource && dataSource.getId(),
				includeParents: (options && options.includeParents === true) ? true : false, //Default including parents to false (not applicable to charts)
				ignoreEdgeSelections: (options && options.ignoreEdgeSelections === true) ? true : false
			});
			return (selected.hasSelections()) ? selected.getAPI() : null;
		}
	}

	return DataPointSelections;
});
